var { useState, useCallback, useMemo, useRef } = require('react');
var { useSelector } = require('react-redux');
var { useTranslation } = require('react-i18next');
var { validateEmail } = require('@managers/authenticationManager');
var {
  sendSupportViaApi,
  MAX_SUPPORT_ATTACHMENTS,
} = require('@managers/zendeskSupportApi');
var zendeskManager = require('@managers/zendeskManager');

function useSupportForm() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const userDetails = useSelector(
    (state) => state.authentication_data.user_details,
  );

  const isAuthenticated = !!userDetails?.email;

  const [message, setMessage] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');

  const accountDisplayName = useMemo(() => {
    if (!userDetails) {
      return '';
    }
    return [userDetails.first_name, userDetails.last_name]
      .filter(Boolean)
      .join(' ');
  }, [userDetails]);

  const displayName = isAuthenticated ? accountDisplayName : guestName;
  const email = isAuthenticated ? userDetails?.email || '' : guestEmail;

  const handleNameChange = useCallback((e) => {
    setGuestName(e.target.value);
  }, []);

  const handleEmailChange = useCallback((e) => {
    setGuestEmail(e.target.value);
    setResult(null);
    setErrorDetail('');
  }, []);

  const handleMessageChange = useCallback((e) => {
    setMessage(e.target.value);
    setResult(null);
    setErrorDetail('');
  }, []);

  const resetStatus = useCallback(() => {
    setResult(null);
    setErrorDetail('');
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleAddFiles = useCallback((fileList) => {
    if (!fileList || !fileList.length) {
      return;
    }

    // Snapshot before clearing <input> — FileList is live and empties when value resets.
    const incoming = Array.from(fileList);

    setAttachments((prev) => {
      const remaining = MAX_SUPPORT_ATTACHMENTS - prev.length;
      if (remaining <= 0) {
        return prev;
      }

      const next = [...prev];
      incoming.slice(0, remaining).forEach((file) => {
        next.push({
          id: `${file.name}-${file.size}-${file.lastModified}-${Math.random()}`,
          file,
        });
      });
      return next;
    });

    setResult(null);
    setErrorDetail('');
  }, []);

  const handleFileInputChange = useCallback(
    (e) => {
      const { files } = e.target;
      if (files && files.length) {
        handleAddFiles(files);
      }
      // Defer reset so change handler finishes before the live FileList is cleared.
      window.setTimeout(() => {
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 0);
    },
    [handleAddFiles],
  );

  const handleRemoveAttachment = useCallback((id) => {
    setAttachments((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const openFilePicker = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleSubmit = useCallback(
    async (e) => {
      if (e && e.preventDefault) {
        e.preventDefault();
      }
      const trimmed = message.trim();
      if (!trimmed || loading || !email) {
        return;
      }

      setLoading(true);
      setResult(null);
      setErrorDetail('');

      try {
        // Authenticated users go through the backend agent path (no email
        // verification). Guests submit anonymously via the public Zendesk
        // requests API, which triggers an email verification link.
        const ok = isAuthenticated
          ? await sendSupportViaApi(trimmed, {
              source: 'Web App',
              files: attachments.map((item) => item.file),
            })
          : await zendeskManager.sendMessage(displayName, email, trimmed, {
              platform: 'web',
            });
        if (ok) {
          setResult('success');
          setMessage('');
          clearAttachments();
        } else {
          setResult('error');
          setErrorDetail(t('support_message_failed'));
        }
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          err?.message ||
          t('support_message_failed');
        setResult('error');
        setErrorDetail(
          typeof detail === 'string' ? detail : JSON.stringify(detail),
        );
      } finally {
        setLoading(false);
      }
    },
    [
      message,
      loading,
      email,
      attachments,
      clearAttachments,
      t,
      isAuthenticated,
      displayName,
    ],
  );

  const canSubmit =
    message.trim().length > 0 &&
    !loading &&
    !!email &&
    (isAuthenticated || validateEmail(email));
  const canAddMoreFiles = attachments.length < MAX_SUPPORT_ATTACHMENTS;

  return {
    t,
    isAuthenticated,
    displayName,
    email,
    message,
    attachments,
    loading,
    result,
    errorDetail,
    canSubmit,
    canAddMoreFiles,
    fileInputRef,
    handleMessageChange,
    handleNameChange,
    handleEmailChange,
    handleSubmit,
    handleAddFiles,
    handleFileInputChange,
    handleRemoveAttachment,
    openFilePicker,
    resetStatus,
    clearAttachments,
    maxAttachments: MAX_SUPPORT_ATTACHMENTS,
  };
}

module.exports = { useSupportForm };
